import { initializePaddle, Paddle, Theme } from '@paddle/paddle-js'
import { SUBSCRIPTION_PACKAGES } from 'constants/ai'
import { useAuthUser } from 'next-firebase-auth'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTheme } from 'styled-components'
import { assertProd } from 'utils/assert-prod'
import { saveCancellationFeedback } from 'utils/database/save-cancellation-feedback'
import { ActionType, PricingPeriodType } from 'views/pricing'

import { cancelSubscription, createNewTransaction, switchSubscription } from './api'
import { CancellationModal } from './CancellationModal'
import { FeedbackModal } from './FeedbackModal'
import { PlanSwitchModal } from './PlanSwitchModal'

// Define the metadata type
// This is the metadata that will be passed to the checkout modal
export type CheckoutMetadata = {
  action: ActionType
  pricingPeriod: PricingPeriodType
  planType?: (typeof SUBSCRIPTION_PACKAGES)[keyof typeof SUBSCRIPTION_PACKAGES]
}

/**
 * Open the Paddle checkout dialog
 * This is a wrapper around the Paddle.Checkout.open method
 * It is used to open the checkout dialog with the given transaction ID, theme, and success URL
 * @param paddle - The Paddle instance
 * @param transactionId - The transaction ID
 * @param theme - The theme
 * @param successUrl - The success URL
 */
const handleOpenPaddleCheckoutDialog = (paddle: Paddle, transactionId: string, theme: string, successUrl: string) => {
  return paddle.Checkout.open({
    transactionId,
    settings: {
      theme: theme as Theme,
      successUrl: successUrl,
    },
  })
}

export function Checkout({
  children,
}: {
  children: React.ReactNode | ((handleCheckout: (metadata: CheckoutMetadata) => void) => React.ReactNode)
}) {
  const authUser = useAuthUser()
  const uid = authUser.id
  const email = authUser.email
  const theme = useTheme()

  const [paddle, setPaddle] = useState<Paddle>()
  const [showCancellationModal, setShowCancellationModal] = useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [showPlanSwitchModal, setShowPlanSwitchModal] = useState(false)
  const [switchMetadata, setSwitchMetadata] = useState<CheckoutMetadata | null>(null)

  useEffect(() => {
    initializePaddle({
      environment: process.env.NODE_ENV === 'production' ? 'production' : 'sandbox',
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      token: process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN!,
      pwAuth: process.env.NEXT_PUBLIC_PADDLE_RETAIN_KEY || undefined,
      pwCustomer: uid
        ? {
            id: uid,
            email: email || undefined,
          }
        : undefined,
    }).then((paddle) => {
      setPaddle(paddle)
    })
  }, [uid, email])

  /**
   * Handle the cancellation of the subscription
   * @returns void
   * @throws Error if the user is not authenticated
   */
  const handleCancellation = async (): Promise<void> => {
    if (!uid) return

    try {
      const { success } = await cancelSubscription(uid)

      if (!success) {
        throw new Error('Failed to cancel subscription')
      }

      toast.success('Your subscription has been cancelled successfully.')
      setShowCancellationModal(false)

      // Show feedback modal after successful cancellation
      setShowFeedbackModal(true)
    } catch (error) {
      toast.error('Failed to cancel subscription. Please try again later.')
      assertProd(false, `Failed to cancel subscription for user ${uid}`)
    }
  }

  /**
   * Handle feedback submission
   * @param feedback - The feedback text from the user
   */
  const handleFeedbackSubmit = async (feedback: string): Promise<void> => {
    if (!uid || !email) return

    // Save feedback to Firebase Realtime Database
    await saveCancellationFeedback({
      userId: uid,
      email: email,
      timestamp: Date.now(),
      feedback: feedback.trim(),
    })

    toast.success('Thank you for your feedback!')
    setShowFeedbackModal(false)

    // Reload the page after a short delay
    setTimeout(() => {
      window.location.reload()
    }, 1000)
  }

  /**
   * Handle the subscription plan switch
   * @returns void
   * @throws Error if the user is not authenticated
   */
  const handlePlanSwitch = async (): Promise<void> => {
    if (!uid || !switchMetadata || !paddle) return

    try {
      const { success, transactionId } = await switchSubscription({ ...switchMetadata, uid, email })

      if (!success) {
        throw new Error('Failed to switch subscription')
      }

      if (transactionId) {
        handleOpenPaddleCheckoutDialog(
          paddle,
          transactionId,
          theme.palette.mode,
          `${window.location.origin}/profile#manage-subscription`
        )
      }

      setShowPlanSwitchModal(false)
      setSwitchMetadata(null)
    } catch (error) {
      toast.error('Failed to switch subscription. Please try again later.')
      setShowPlanSwitchModal(false)
      setSwitchMetadata(null)
    }
  }

  /**
   * Handle the checkout of the subscription
   * @param metadata - The metadata for the checkout
   * @returns void
   * @throws Error if the user is not authenticated
   * @throws Error if the paddle is not initialized
   * @throws Error if the transaction ID is not returned
   */
  const handleCheckout = async (metadata: CheckoutMetadata): Promise<void> => {
    if (!paddle || !uid) return

    const hasCancelRequest = metadata.action === 'CANCEL'
    const hasPlanSwitchRequest = metadata.action === 'SWITCH'

    // If the planId is free, open the cancellation confirmation modal
    if (hasCancelRequest) {
      setShowCancellationModal(true)
      return
    }

    // If the planId is not free and the action is switch, open the plan switch confirmation modal
    if (hasPlanSwitchRequest) {
      setSwitchMetadata(metadata)
      setShowPlanSwitchModal(true)
      return
    }
    try {
      // Create a transaction
      // This will create a transaction in Paddle and return the transaction ID
      const { transactionId } = await createNewTransaction({ ...metadata, uid, email })

      if (!transactionId) {
        throw new Error('Failed to create transaction')
      }

      handleOpenPaddleCheckoutDialog(
        paddle,
        transactionId,
        theme.palette.mode,
        `${window.location.origin}/profile#manage-subscription`
      )
    } catch (error) {
      toast.error('An error occurred while processing your subscription. Please try again later.')
      return
    }
  }

  return (
    <div>
      {/* If the children is a function, call it with the handleCheckout function as an argument */}
      {typeof children === 'function' ? children(handleCheckout) : children}
      <CancellationModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
        onConfirm={handleCancellation}
      />
      <FeedbackModal isOpen={showFeedbackModal} onSubmit={handleFeedbackSubmit} />
      {switchMetadata && (
        <PlanSwitchModal
          isOpen={showPlanSwitchModal}
          onClose={() => {
            setShowPlanSwitchModal(false)
            setSwitchMetadata(null)
          }}
          onConfirm={handlePlanSwitch}
          metadata={switchMetadata}
        />
      )}
    </div>
  )
}
